import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowUpRight, MoveRight, Phone, Target, Rocket } from "lucide-react";
import { Reveal, MaskedLines } from "@/components/Reveal";
import Marquee from "@/components/Marquee";
import CTABanner from "@/components/CTABanner";
import Founder from "@/components/Founder";

const IMG = {
  meta: "https://images.unsplash.com/photo-1585314062340-f1a5a7c9328d?crop=entropy&cs=srgb&fm=jpg&q=85",
  google: "https://images.unsplash.com/photo-1658953229664-e8d5ebd039ba?crop=entropy&cs=srgb&fm=jpg&q=85",
};

const STATS = [
  { value: "US REGISTERED", label: "Wyoming LLC" },
  { value: "ECOMMERCE", label: "Built around online growth" },
  { value: "PERFORMANCE", label: "Measurable commercial outcomes" },
  { value: "INTERNATIONAL", label: "US registered. Internationally focused." },
];

const MANIFESTO = [
  {
    n: "01",
    title: "Uncompromising ROAS",
    copy: "Every dollar we deploy is accountable. Budgets live inside a measurement stack — server-side tracking, attribution modeling and weekly incrementality checks — so scaling decisions are made on evidence, not opinions.",
  },
  {
    n: "02",
    title: "Creative Velocity",
    copy: "On Meta, the auction rewards novelty. Our creative engine ships a steady cadence of hooks, angles and formats, then lets the algorithm vote. Winners get budget; everything else gets retired.",
  },
  {
    n: "03",
    title: "Algorithmic Precision",
    copy: "On Google, intent is bought at the keyword level. We mine query data, shield campaigns with negative keywords and feed Performance Max cleaner asset signals than your competitors ever will.",
  },
];

const SERVICES = [
  {
    tag: "Service / 01",
    title: "Meta Ads Architecture",
    copy: "Facebook & Instagram acquisition systems: creative testing frameworks, Conversion API tracking, dynamic product catalogs and Advantage+ scaling — built as one engine, not a bundle of ad sets.",
    pills: ["Creative Testing", "CAPI Tracking", "Catalog Ads", "Advantage+"],
    img: IMG.meta,
    to: "/services/meta-ads",
    tid: "service-meta-cta",
  },
  {
    tag: "Service / 02",
    title: "Google Ads Dominance",
    copy: "Search, Shopping and Performance Max engineered for high-intent capture — keyword mining, asset optimization and offline conversion imports that keep the bidding algorithms honest.",
    pills: ["Search Precision", "Performance Max", "YouTube", "Shopping"],
    img: IMG.google,
    to: "/services/google-ads",
    tid: "service-google-cta",
  },
];

const APPROACH = [
  {
    tag: "Paid Media",
    title: "META + GOOGLE",
    copy: "Performance marketing built around customer acquisition, efficiency and profitable growth.",
    focus: ["Paid Social", "Paid Search", "Campaign Structure", "Creative Testing"],
  },
  {
    tag: "Qualified Demand",
    title: "NOT JUST MORE LEADS. BETTER LEADS.",
    copy: "We look beyond traffic and cost per lead to focus on the quality and commercial potential of the demand being generated.",
    focus: ["Lead Quality", "Customer Intent", "Conversion", "Commercial Value"],
  },
  {
    tag: "Analytics & Optimization",
    title: "MEASURE. LEARN. IMPROVE.",
    copy: "Clear performance analysis helps identify what is working, what is not, and where the next growth opportunity exists.",
    focus: ["Performance Analysis", "Attribution", "Testing", "Continuous Optimization"],
  },
];

const FUNNEL = ["Traffic", "Qualified leads", "Customers", "Revenue"];

const usd = (n) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n);

const VIZ_BARS = [34, 52, 44, 66, 55, 74, 62, 84, 70, 92, 78, 100];

const AbstractViz = () => (
  <div className="relative rounded-2xl overflow-hidden border border-white/10 bg-surface h-[340px] sm:h-[420px] lg:h-[480px]" data-testid="hero-abstract-viz">
    <div
      className="absolute inset-0"
      style={{ background: "radial-gradient(60% 50% at 70% 30%, rgba(255,77,0,0.10), transparent 70%)" }}
      aria-hidden="true"
    />
    <div className="absolute top-5 left-5 flex flex-wrap gap-2 z-10">
      {["META ADS", "GOOGLE ADS"].map((t) => (
        <span key={t} className="font-mono text-[10px] uppercase tracking-[0.2em] text-gray-300 border border-white/10 bg-white/5 rounded-full px-3 py-1.5">
          {t}
        </span>
      ))}
    </div>
    <svg className="absolute inset-x-0 top-10 w-full h-24" viewBox="0 0 400 100" preserveAspectRatio="none" aria-hidden="true">
      <motion.path
        d="M0 82 C 50 74, 80 80, 120 58 S 200 42, 250 32 S 340 20, 400 12"
        fill="none"
        stroke="#FF6B26"
        strokeWidth="1.5"
        initial={{ pathLength: 0 }}
        animate={{ pathLength: 1 }}
        transition={{ duration: 1.8, delay: 0.6, ease: "easeInOut" }}
      />
    </svg>
    <div className="absolute inset-x-6 sm:inset-x-8 bottom-9 top-24 flex items-end gap-2 sm:gap-3">
      {VIZ_BARS.map((h, i) => (
        <motion.div
          key={i}
          initial={{ height: "6%" }}
          animate={{ height: `${h}%` }}
          transition={{ duration: 0.9, delay: 0.4 + i * 0.06, ease: [0.16, 1, 0.3, 1] }}
          className="flex-1 rounded-t-md bg-gradient-to-t from-flame/10 via-flame/30 to-flame/80"
        />
      ))}
    </div>
    <p className="absolute top-5 right-5 font-mono text-[10px] uppercase tracking-[0.2em] text-white/35">
      Abstract — decorative
    </p>
  </div>
);

function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 90]);

  return (
    <section ref={ref} className="relative flex items-center overflow-hidden pt-32 pb-16 sm:pt-40 lg:pt-0 lg:min-h-screen" data-testid="home-hero">
      <div className="absolute inset-0 hero-glow" aria-hidden="true" />
      <div className="relative w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-12 lg:gap-8 items-center">
        <div className="lg:col-span-7">
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="font-mono text-xs sm:text-sm uppercase tracking-[0.25em] text-flame mb-6"
          >
            Vibera Group LLC — Performance Marketing
          </motion.p>
          <h1 className="font-syne text-4xl sm:text-6xl lg:text-7xl font-extrabold tracking-tight leading-[1.05] text-white">
            <MaskedLines
              delay={0.2}
              lines={[<>We turn ad spend</>, <>into <span className="text-flame">revenue engines.</span></>]}
            />
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.7 }}
            className="mt-7 text-lg sm:text-xl text-gray-300 leading-relaxed max-w-xl"
          >
            Vibera Group is an American performance marketing firm built around two disciplines:
            Meta Ads and Google Ads. No retainers padded with deliverables — just acquisition
            systems engineered for measurable commercial outcomes.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.85 }}
            className="mt-10 flex flex-col sm:flex-row gap-4"
          >
            <Link
              to="/contact"
              data-testid="hero-cta-audit"
              className="group inline-flex justify-center items-center gap-2 rounded-full bg-flame px-8 py-4 font-semibold text-white transition-all hover:bg-flame-hover hover:scale-[1.02]"
            >
              Book Strategic Audit
              <ArrowUpRight className="h-5 w-5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
            <a
              href="tel:+13075007253"
              data-testid="hero-cta-phone"
              className="inline-flex justify-center items-center gap-2 rounded-full border border-white/20 px-8 py-4 font-semibold text-white transition-colors hover:border-flame hover:text-flame"
            >
              <Phone className="h-5 w-5" />
              +1 (307) 500-7253
            </a>
          </motion.div>
        </div>
        <motion.div style={{ y: imgY }} className="relative lg:col-span-5 hidden sm:block">
          <AbstractViz />
          <div className="absolute -bottom-6 -left-6 rounded-2xl border border-white/10 bg-surface-elevated/95 backdrop-blur-md px-6 py-5 shadow-2xl max-w-[280px]">
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-flame">Performance focus</p>
            <p className="mt-2 font-syne text-lg font-bold text-white leading-tight">Meta + Google</p>
            <p className="mt-1.5 text-xs text-gray-400 leading-relaxed">
              Paid media strategy built around measurable commercial outcomes.
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

const StatBand = () => (
  <section className="border-y border-white/10" data-testid="stat-band">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 sm:grid-cols-2 gap-x-6 lg:gap-x-0 lg:grid-cols-4">
      {STATS.map((s, i) => (
        <Reveal key={s.label} delay={i * 0.08} className={`py-10 sm:py-14 lg:pr-3 xl:pr-4 ${i > 0 ? "lg:border-l lg:border-white/10 lg:pl-6 xl:pl-8" : ""}`}>
          <p className="font-syne text-base sm:text-xl lg:text-lg xl:text-2xl font-bold tracking-tight text-white">{s.value}</p>
          <p className="mt-2 font-mono text-[10px] sm:text-xs uppercase tracking-[0.2em] text-gray-500">{s.label}</p>
        </Reveal>
      ))}
    </div>
  </section>
);

const Manifesto = () => (
  <section className="py-20 sm:py-28 lg:py-36" data-testid="manifesto-section">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <Reveal>
        <p className="font-mono text-xs sm:text-sm uppercase tracking-[0.25em] text-flame">The Vibera Doctrine</p>
        <h2 className="mt-4 font-syne text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white">
          Three principles. Zero fluff.
        </h2>
      </Reveal>
      <div className="mt-16">
        {MANIFESTO.map((m, i) => (
          <Reveal key={m.n} delay={0.05 * i}>
            <div className="grid md:grid-cols-12 gap-4 md:gap-8 py-10 sm:py-14 border-t border-white/10 group">
              <p className="md:col-span-2 font-mono text-2xl sm:text-3xl font-bold text-flame/70 group-hover:text-flame transition-colors">
                {m.n}/
              </p>
              <h3 className="md:col-span-4 font-syne text-xl sm:text-2xl font-bold text-white">{m.title}</h3>
              <p className="md:col-span-6 text-base text-gray-400 leading-relaxed">{m.copy}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const Services = () => (
  <section className="py-20 sm:py-28 lg:py-36" data-testid="services-section">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <Reveal>
        <p className="font-mono text-xs sm:text-sm uppercase tracking-[0.25em] text-flame">What we do</p>
        <h2 className="mt-4 font-syne text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white max-w-3xl">
          Two disciplines. Executed obsessively.
        </h2>
      </Reveal>
      <div className="mt-14 grid lg:grid-cols-2 gap-8">
        {SERVICES.map((s, i) => (
          <Reveal key={s.title} delay={0.1 * i}>
            <Link
              to={s.to}
              data-testid={s.tid}
              className="group block rounded-2xl border border-white/10 bg-surface overflow-hidden transition-colors hover:border-flame/50"
            >
              <div className="relative h-56 sm:h-64 overflow-hidden">
                <img
                  src={s.img}
                  alt={s.title}
                  className="w-full h-full object-cover opacity-80 transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-surface via-surface/30 to-transparent" aria-hidden="true" />
                <span className="absolute top-5 left-5 font-mono text-xs uppercase tracking-[0.2em] text-flame">{s.tag}</span>
              </div>
              <div className="p-7 sm:p-9">
                <h3 className="font-syne text-2xl font-bold text-white">{s.title}</h3>
                <p className="mt-3 text-sm sm:text-base text-gray-400 leading-relaxed">{s.copy}</p>
                <div className="mt-6 flex flex-wrap gap-2">
                  {s.pills.map((p) => (
                    <span key={p} className="rounded-full border border-white/10 bg-white/5 px-3.5 py-1.5 text-xs text-gray-300">
                      {p}
                    </span>
                  ))}
                </div>
                <span className="mt-7 inline-flex items-center gap-2 text-sm font-semibold text-flame">
                  Explore {s.title.split(" ")[0]} Ads
                  <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                </span>
              </div>
            </Link>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

function Calculator() {
  const [spend, setSpend] = useState(50000);
  const [roas, setRoas] = useState(4.5);
  const monthly = spend * roas;
  const annual = monthly * 12;
  const spendFill = `${(((spend - 10000) / 240000) * 100).toFixed(1)}%`;
  const roasFill = `${(((roas - 2) / 6) * 100).toFixed(1)}%`;

  return (
    <section className="py-20 sm:py-28 lg:py-36" data-testid="roi-calculator-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Reveal>
          <p className="font-mono text-xs sm:text-sm uppercase tracking-[0.25em] text-flame">Interactive model</p>
          <h2 className="mt-4 font-syne text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white">
            Run your own numbers.
          </h2>
        </Reveal>
        <Reveal delay={0.1}>
          <div className="mt-12 grid lg:grid-cols-2 gap-10 rounded-3xl border border-white/10 bg-surface p-6 sm:p-10">
            <div className="space-y-10">
              <div>
                <div className="flex items-center justify-between mb-4">
                  <label htmlFor="spend" className="text-sm text-gray-300 flex items-center gap-2">
                    <Target className="h-4 w-4 text-flame" />Monthly ad spend
                  </label>
                  <span className="font-mono text-sm font-bold text-flame tabular-nums">{usd(spend)}</span>
                </div>
                <input
                  id="spend"
                  type="range"
                  min="10000"
                  max="250000"
                  step="5000"
                  value={spend}
                  onChange={(e) => setSpend(Number(e.target.value))}
                  data-testid="roi-calculator-slider"
                  style={{ "--fill": spendFill }}
                />
              </div>
              <div>
                <div className="flex items-center justify-between mb-4">
                  <label htmlFor="roas" className="text-sm text-gray-300 flex items-center gap-2">
                    <Rocket className="h-4 w-4 text-flame" />Expected blended ROAS
                  </label>
                  <span className="font-mono text-sm font-bold text-flame tabular-nums">{roas.toFixed(1)}x</span>
                </div>
                <input
                  id="roas"
                  type="range"
                  min="2"
                  max="8"
                  step="0.1"
                  value={roas}
                  onChange={(e) => setRoas(Number(e.target.value))}
                  data-testid="roi-calculator-roas-slider"
                  style={{ "--fill": roasFill }}
                />
              </div>
              <p className="text-xs text-gray-500 leading-relaxed">
                The projections above are generated entirely from your own inputs as an interactive
                planning exercise — not a promise, benchmark or claim about Vibera results.
              </p>
            </div>
            <div className="grid gap-4 content-center">
              {[
                { label: "Projected monthly revenue", value: usd(monthly), tid: "roi-monthly-revenue" },
                { label: "Projected annual revenue", value: usd(annual), tid: "roi-annual-revenue" },
                { label: "Return on every $1 deployed", value: `${roas.toFixed(1)}x`, tid: "roi-return-per-dollar" },
              ].map((o) => (
                <div key={o.label} className="rounded-2xl border border-white/10 bg-surface-elevated px-6 py-5 flex items-center justify-between gap-4">
                  <p className="text-sm text-gray-400">{o.label}</p>
                  <p className="font-syne text-xl sm:text-2xl font-extrabold text-white tabular-nums" data-testid={o.tid}>
                    {o.value}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

const Approach = () => (
  <section className="py-20 sm:py-28 lg:py-36" data-testid="approach-section">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <Reveal>
        <p className="font-mono text-xs sm:text-sm uppercase tracking-[0.25em] text-flame">Approach</p>
        <h2 className="mt-4 font-syne text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white">
          Built for performance.
        </h2>
        <p className="mt-5 text-base sm:text-lg text-gray-400 max-w-2xl">
          A commercially focused approach to digital growth — from strategy and execution to continuous optimization.
        </p>
      </Reveal>
      <div className="mt-14 grid md:grid-cols-3 gap-6 lg:gap-8">
        {APPROACH.map((c, i) => (
          <Reveal key={c.title} delay={0.1 * i} className="h-full">
            <div className="h-full rounded-2xl border border-white/10 bg-surface p-8 flex flex-col transition-colors hover:border-flame/40">
              <p className="font-mono text-xs uppercase tracking-[0.2em] text-flame">{c.tag}</p>
              <h3 className="mt-6 font-syne text-xl sm:text-2xl font-extrabold text-white">{c.title}</h3>
              <p className="mt-4 text-sm text-gray-400 leading-relaxed flex-1">{c.copy}</p>
              <div className="mt-6 pt-6 border-t border-white/10">
                <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-gray-500 mb-3">Focus areas</p>
                <div className="flex flex-wrap gap-2">
                  {c.focus.map((p) => (
                    <span key={p} className="rounded-full border border-white/10 bg-white/5 px-3.5 py-1.5 text-xs text-gray-300">
                      {p}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </Reveal>
        ))}
      </div>
      <Reveal delay={0.1}>
        <div className="mt-8 lg:mt-10 rounded-3xl border border-white/10 bg-surface p-8 sm:p-12" data-testid="quality-band">
          <p className="font-mono text-xs uppercase tracking-[0.25em] text-flame">Quality over volume</p>
          <h3 className="mt-4 font-syne text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-white max-w-3xl">
            We don't optimize for traffic. We optimize for business.
          </h3>
          <div className="mt-6 grid md:grid-cols-2 gap-4 md:gap-10 max-w-4xl text-sm sm:text-base text-gray-400 leading-relaxed">
            <p>
              More traffic and cheaper leads do not necessarily create better business results.
              Vibera looks beyond clicks and cost per lead to understand the quality and commercial
              value of the demand being generated.
            </p>
            <p>
              We focus on attracting the right potential customers and continuously optimize
              campaigns based on what happens further down the funnel.
            </p>
          </div>
          <div className="mt-10 flex flex-wrap items-center gap-x-4 gap-y-3 font-mono text-xs sm:text-sm uppercase tracking-[0.2em]" data-testid="funnel-progression">
            {FUNNEL.map((s, i) => (
              <span key={s} className="flex items-center gap-4">
                <span className={i === FUNNEL.length - 1 ? "text-flame font-bold" : i === 0 ? "text-gray-500" : "text-gray-300"}>
                  {s}
                </span>
                {i < FUNNEL.length - 1 && <MoveRight className="h-4 w-4 text-flame/70 shrink-0" aria-hidden="true" />}
              </span>
            ))}
          </div>
        </div>
      </Reveal>
    </div>
  </section>
);

const OwnerPerspective = () => (
  <section className="py-20 sm:py-28 lg:py-36" data-testid="owner-perspective-section">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <Reveal>
        <p className="font-mono text-xs sm:text-sm uppercase tracking-[0.25em] text-flame">Why Vibera exists</p>
        <h2 className="mt-4 font-syne text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white max-w-3xl">
          Marketing from an <span className="text-flame">owner's perspective.</span>
        </h2>
      </Reveal>
      <Reveal delay={0.1}>
        <div className="mt-8 grid md:grid-cols-2 gap-6 md:gap-10 max-w-4xl text-base text-gray-400 leading-relaxed">
          <p>
            Vibera combines performance marketing with the commercial perspective of people who
            understand what it means to build and operate a business.
          </p>
          <p>
            We focus on marketing as part of the wider business — connecting advertising decisions
            with customer acquisition, revenue and sustainable growth.
          </p>
        </div>
      </Reveal>
    </div>
  </section>
);

const Home = () => (
  <>
    <Hero />
    <StatBand />
    <div className="mt-20 sm:mt-28">
      <Marquee
        items={[
          "META ADS SCALING",
          "GOOGLE SEARCH DOMINANCE",
          "OMNICHANNEL ROAS",
          "ATTRIBUTION MODELLING",
          "CREATIVE VELOCITY",
          "PERFORMANCE MAX",
        ]}
      />
    </div>
    <Manifesto />
    <Services />
    <Calculator />
    <Approach />
    <OwnerPerspective />
    <Founder />
    <CTABanner
      title="Your market is bidding on your customers right now."
      sub="Book a strategic audit. We'll map exactly where the budget leaks are — and the fastest path to profitable scale."
      testPrefix="home-cta"
    />
  </>
);

export default Home;
